import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DataContext } from '../../contexto/ContextoDatos';
import styles from './Encabezado.module.css';

const FeaturedProductImage = ({ slug, name }) => {
  const [imgError, setImgError] = useState(false);
  const imgSrc = `/imagenes/productos/${slug}-1.webp`;

  if (imgError) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
    );
  }

  return (
    <img 
      src={imgSrc} 
      alt={name} 
      style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '4px', borderRadius: '4px' }}
      onError={() => setImgError(true)}
    />
  );
};

export const Navigation = ({ mobile, closeMobileMenu }) => {
  const { services, products } = useContext(DataContext);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSection, setActiveSection] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const bestSellers = useMemo(() => products.filter(p => p.best_seller), [products]);
  const numPages = Math.ceil(bestSellers.length / 2);
  const [currentPage, setCurrentPage] = useState(0);
  const [isHoveringCarousel, setIsHoveringCarousel] = useState(false);
  const [macroTimer, setMacroTimer] = useState(0);
  const [isHoveringMacro, setIsHoveringMacro] = useState(false);

  useEffect(() => {
    if (isHoveringMacro) return;
    const interval = setInterval(() => {
      setMacroTimer(prev => prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [isHoveringMacro]);

  useEffect(() => {
    if (numPages <= 1 || isHoveringCarousel) return;
    const interval = setInterval(() => {
      setCurrentPage(prev => (prev + 1) % numPages);
    }, 5000);
    return () => clearInterval(interval);
  }, [numPages, isHoveringCarousel]);

  const featuredProducts = useMemo(() => {
    if (bestSellers.length === 0) return [];
    const start = currentPage * 2;
    return bestSellers.slice(start, start + 2);
  }, [bestSellers, currentPage]);

  const MACRO_CATEGORIES = [
    {
      title: "Aspiración y Extracción",
      slug: "aspiracion-y-extraccion",
      links: [
        { name: "Aspiradoras Industriales", slug: "aspiradoras-industriales" },
        { name: "Aspiradoras Comerciales", slug: "aspiradoras-comerciales" },
        { name: "Colectores de Polvo", slug: "colectores-y-ciclones-de-polvo" },
      ]
    },
    {
      title: "Tratamiento de Pisos",
      slug: "tratamiento-de-pisos",
      links: [
        { name: "Fregadoras de Piso", slug: "fregadoras-de-piso" },
        { name: "Pulidoras de Piso", slug: "pulidoras-de-piso" },
        { name: "Limpiadores a Vapor", slug: "limpiadores-a-vapor-seco" },
      ]
    },
    {
      title: "Alta Presión",
      slug: "alta-presion",
      links: [
        { name: "Hidrolavadoras Industriales", slug: "hidrolavadoras-industriales" },
        { name: "Super Alta Presión", slug: "hidrolavadoras-de-super-alta-presion" },
        { name: "Equipos de Lavado SAP", slug: "hidrolavadoras-equipos-de-lavado-super-alta-presion" },
        { name: "Limpieza de Tuberías", slug: "limpieza-de-tuberias" },
        { name: "Accesorios de Alta Presión", slug: "hidrolavadoras-accesorios" },
      ]
    },
    {
      title: "Lavandería",
      slug: "lavanderia",
      links: [
        { name: "Equipos Comerciales", slug: "lavanderia-comercial" },
        { name: "Pequeña Capacidad", slug: "lavanderia-de-pequena-capacidad" },
        { name: "Lavadoras Rígidas", slug: "lavanderia-industrial-lavadoras-rigidas" },
        { name: "Lavadoras Flotantes", slug: "lavanderia-industrial-lavadoras-flotantes" },
        { name: "Barrera Sanitaria", slug: "lavanderia-industrial-barrera-sanitaria" },
        { name: "Secadoras", slug: "lavanderia-industrial-secadoras" },
        { name: "Planchadoras de Rodillos", slug: "lavanderia-industrial-planchadoras-de-rodillos" },
        { name: "Calandras con Plegadora", slug: "lavanderia-industrial-calandras-con-plegadora" },
        { name: "Mesas y Acabados", slug: "lavanderia-industrial-acabados" },
        { name: "Domus Compact Line", slug: "lavanderia-industrial-domus-compact-line" },
        { name: "Recuperación Ecotank", slug: "lavanderia-industrial-ecotank-recuperacion-de-agua" },
      ]
    }
  ];

  // Scrollspy: activa la sección cuyo top pasó más recientemente el 40% del viewport
  useEffect(() => {
    if (mobile) return;

    const onScroll = () => {
      // Si no estamos en la página de inicio, marcamos la sección según la URL
      const path = location.pathname;
      if (path !== '/') {
        if (path.startsWith('/servicios')) {
          setActiveSection('services');
        } else if (path.startsWith('/productos')) {
          setActiveSection('products');
        } else if (path.startsWith('/proyectos')) {
          setActiveSection('proyectos');
        } else if (path.startsWith('/contacto')) {
          setActiveSection('contact');
        } else if (path.startsWith('/nosotros')) {
          setActiveSection('nosotros');
        } else {
          setActiveSection('');
        }
        return;
      }

      const ids = ['historia', 'services', 'products', 'seccion-proyectos', 'location', 'contact'];
      const trigger = window.innerHeight * 0.4;
      let best = '';
      let bestTop = -Infinity;

      ids.forEach(id => {
        const el = document.getElementById(id);
        if (!el) return;
        const { top } = el.getBoundingClientRect();
        if (top < trigger && top > bestTop) {
          bestTop = top;
          best = id;
        }
      });

      setActiveSection(best);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Run once on mount or location change
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [mobile, location.pathname]);

  const closeMenu = () => {
    setActiveDropdown(null);
    if (closeMobileMenu) closeMobileMenu();
    if (document.activeElement) document.activeElement.blur();
  };

  const handleProductCategoryClick = (e) => {
    closeMenu();

    if (location.pathname === '/productos') {
      setTimeout(() => {
        const listElement = document.getElementById('product-list');
        if (listElement) {
          const y = listElement.getBoundingClientRect().top + window.scrollY - 100;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 50);
    }
  };

  const getVisibleDots = () => {
    if (numPages <= 5) return Array.from({ length: numPages }, (_, i) => i);
    
    let start = currentPage - 2;
    let end = currentPage + 2;
    
    if (start < 0) {
      start = 0;
      end = 4;
    } else if (end >= numPages) {
      end = numPages - 1;
      start = numPages - 5;
    }
    
    const dots = [];
    for (let i = start; i <= end; i++) dots.push(i);
    return dots;
  };

  const handleDotClick = (idx) => {
    setCurrentPage(idx);
  };

  const scrollToSection = (id) => {
    closeMenu();
    if (window.location.pathname === '/') {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  };

  const handleDropdownToggle = (name) => {
    setActiveDropdown(prev => prev === name ? null : name);
  };

  return (
    <nav className={`${styles.navContainer} ${mobile ? styles.mobileNav : ''}`}>
      <ul className={styles.navList}>

        <li className={styles.navItem}>
          <Link
            to="/nosotros"
            className={`${styles.navLink} ${activeSection === 'nosotros' || activeSection === 'historia' ? styles.navLinkActive : ''}`}
            onClick={() => {
              if (closeMobileMenu) closeMobileMenu();
            }}
          >
            Nosotros
          </Link>
        </li>

        <li
          className={`${styles.navItem} ${styles.hasDropdown} ${activeDropdown === 'services' ? styles.active : ''}`}
          onMouseEnter={() => !mobile && setActiveDropdown('services')}
          onMouseLeave={() => !mobile && setActiveDropdown(null)}
        >
          <button
            className={`${styles.dropdownTrigger} ${activeSection === 'services' ? styles.navLinkActive : ''}`}
            onClick={() => {
              if (mobile) { handleDropdownToggle('services'); }
              else { scrollToSection('services'); }
            }}
            aria-haspopup="true"
            aria-expanded={activeDropdown === 'services'}
          >
            Servicios
            <span className={styles.arrow}>▼</span>
          </button>

          <div className={`${styles.dropdownMenu} ${activeDropdown === 'services' ? styles.show : ''}`}>
            <ul className={styles.dropdownList}>
              {services.map((service) => (
                <li key={service.id} className={styles.dropdownItem}>
                  <Link
                    to={`/servicios/${service.slug}`}
                    className={styles.dropdownLink}
                    onClick={closeMenu}
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
              <li className={`${styles.dropdownItem} ${styles.viewAllItem}`}>
                <Link
                  to="/servicios"
                  className={styles.viewAllLink}
                  onClick={closeMenu}
                >
                  Ver Todos los Servicios →
                </Link>
              </li>
            </ul>
          </div>
        </li>

        <li
          className={`${styles.navItem} ${styles.hasDropdown} ${styles.hasMegaMenu} ${activeDropdown === 'products' ? styles.active : ''}`}
          onMouseEnter={() => !mobile && setActiveDropdown('products')}
          onMouseLeave={() => !mobile && setActiveDropdown(null)}
        >
          <button
            className={`${styles.dropdownTrigger} ${activeSection === 'products' ? styles.navLinkActive : ''}`}
            onClick={() => {
              if (mobile) { handleDropdownToggle('products'); }
              else { scrollToSection('products'); }
            }}
            aria-haspopup="true"
            aria-expanded={activeDropdown === 'products'}
          >
            Productos
            <span className={styles.arrow}>▼</span>
          </button>

          <div className={`${styles.dropdownMenu} ${styles.megaMenu} ${activeDropdown === 'products' ? styles.show : ''}`}>
            <div className={styles.megaMenuGrid}>
              <div className={styles.megaMenuLeft}>
                <div className={styles.macroCategoryGrid}>
                  {MACRO_CATEGORIES.map((macro, idx) => {
                    const ITEMS_PER_PAGE = 3;
                    const totalPages = Math.ceil(macro.links.length / ITEMS_PER_PAGE);
                    const currPage = totalPages > 1 ? macroTimer % totalPages : 0;
                    const startIdx = currPage * ITEMS_PER_PAGE;
                    const visibleLinks = macro.links.slice(startIdx, startIdx + ITEMS_PER_PAGE);

                    return (
                      <div 
                        key={idx} 
                        className={styles.macroCategory}
                        onMouseEnter={() => { if (totalPages > 1) setIsHoveringMacro(true); }}
                        onMouseLeave={() => { if (totalPages > 1) setIsHoveringMacro(false); }}
                      >
                        <h4 className={styles.macroTitle}>
                          <Link to={`/productos/macro/${macro.slug}`} className={styles.macroTitleLink} onClick={closeMenu}>
                            {macro.title}
                          </Link>
                          {totalPages > 1 && (
                            <span className={styles.macroPageIndicator}>
                              ({currPage + 1}/{totalPages})
                            </span>
                          )}
                        </h4>
                        <div className={styles.macroListContainer}>
                          <ul className={`${styles.macroList} ${totalPages > 1 ? styles.macroSlideAnim : ''}`} key={currPage}>
                            {visibleLinks.map((link) => (
                              <li key={link.slug} className={styles.macroItem}>
                                <Link
                                  to={`/productos?categorias=${link.slug}`}
                                  className={styles.macroLink}
                                  onClick={handleProductCategoryClick}
                                >
                                  {link.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <Link
                          to={`/productos?categorias=${macro.links.map(l => l.slug).join('|')}`}
                          className={styles.macroViewAll}
                          onClick={handleProductCategoryClick}
                        >
                          Explorar {macro.title.toLowerCase()} →
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div 
                className={styles.megaMenuFeatured}
                onMouseEnter={() => setIsHoveringCarousel(true)}
                onMouseLeave={() => setIsHoveringCarousel(false)}
              >
                <div className={styles.featuredContent}>
                  <h4 className={styles.featuredTitle}>Equipos Destacados</h4>
                  <div className={styles.featuredProductsList}>
                    {featuredProducts.map(product => (
                      <div key={product.id} className={styles.featuredProductCard}>
                        <div className={styles.fpImagePlaceholder}>
                          <FeaturedProductImage slug={product.slug} name={product.name} />
                        </div>
                        <div className={styles.fpInfo}>
                          <span className={styles.fpBrand}>{product.brand}</span>
                          <span className={styles.fpModel}>{product.name}</span>
                          <Link 
                            to={`/productos/${product.slug}`}
                            className={styles.fpLink}
                            onClick={closeMenu}
                          >
                            Ver Equipo
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={styles.carouselDots}>
                    {getVisibleDots().map((idx) => (
                      <button 
                        key={idx} 
                        className={`${styles.dot} ${idx === currentPage ? styles.dotActive : ''}`} 
                        onClick={() => handleDotClick(idx)}
                        aria-label={`Ver página ${idx + 1} de destacados`}
                      />
                    ))}
                  </div>
                  <Link
                    to="/productos"
                    className="btn btn-primary"
                    style={{ marginTop: '16px', display: 'block', textAlign: 'center' }}
                    onClick={handleProductCategoryClick}
                  >
                    Ver Catálogo Completo
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </li>

        {/* Proyectos */}
        <li className={styles.navItem}>
          <Link
            to="/proyectos"
            className={`${styles.navLink} ${activeSection === 'proyectos' || activeSection === 'seccion-proyectos' ? styles.navLinkActive : ''}`}
            onClick={closeMenu}
          >
            Proyectos
          </Link>
        </li>

        {/* Ubicación */}
        <li className={styles.navItem}>
          <button
            className={`${styles.navLink} ${activeSection === 'location' ? styles.navLinkActive : ''}`}
            onClick={() => scrollToSection('location')}
          >
            Ubicación
          </button>
        </li>

        {/* Contacto */}
        <li className={styles.navItem}>
          <button
            className={`${styles.navLink} ${activeSection === 'contact' ? styles.navLinkActive : ''}`}
            onClick={() => scrollToSection('contact')}
          >
            Contacto
          </button>
        </li>

      </ul>
    </nav>
  );
};
export default Navigation;
